<template>
  <div>
    <gg-card-title class="pt-0">
      Domain Registration
    </gg-card-title>
    <v-row v-if="!isProfileFilled">
      <v-col cols="12">
        <v-alert type="error" dense>
          <router-link :to="{ name: 'profile_info' }" class="white--text fw-bold underline">
            {{ empty_profile_text }}
          </router-link>
        </v-alert>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <v-stepper v-model="step_counter">
          <v-stepper-header>
            <v-stepper-step :complete="step_counter > 1" step="1">
              Search for a domain
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step_counter > 2" step="2">
              Fill account information
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step_counter > 3" step="3">
              Fill payment method
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="4"></v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-card>
                <v-container class="fill-height" fluid>
                  <v-row class="fill-height" >
                    <v-col class="align-center flexbox" cols="12" >
                      <v-form @submit="searchDomain">
                        <gg-text-field
                          v-if="step_counter === 1"
                          outlined
                          clearable
                          label="Enter your domain"
                          color="primary"
                          v-model="search"
                          v-validate="rules.domain"
                          data-vv-name="domain"
                          data-vv-as="Domain"
                          :error-messages="errors.collect('domain')"
                          @click:clear="clearSearchData"
                        ></gg-text-field>
                        <gg-btn type="submit" :loading="buttons.step_one" @click="searchDomain">
                          Domain Lookup
                        </gg-btn>
                      </v-form>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card-text>
                  <div v-if="!isEmpty && domain.available === 1">
                    <p class="subtitle-1">
                      Domain <b>{{ domain.domain }}</b> is available! Only ${{ domain.pricing['1'] }}/year
                    </p>
                    <gg-btn @click="step_counter = 2">
                      Go to the next step
                    </gg-btn>
                  </div>
                  <div v-if="!isEmpty && domain.available !== 1" class="subtitle-1">
                    <p>
                      Domain <b>{{ domain.domain }}</b> is not available.
                      Please try another domain.
                    </p>
                  </div>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <v-stepper-content step="2">
              <div v-if="step_counter === 2">
                <v-card class="app-card card-borderless">
                  <v-card-text>
                    <v-form @submit="accountInformation">
                      <v-container fluid>
                        <!-- Attributes -->
                        <div v-if="isAttributes">
                          <v-row>
                            <div class="app-card-title pl-0">
                              <h3>Domain Attributes</h3>
                            </div>
                            <v-col
                              cols="12"
                              class="pb-0"
                              v-for="(attribute, key) in ExtAttributes.Attribute"
                              :key="`attribute-${key}`"
                            >
                              <component
                                :is="defineAttributeComponent(attribute)"
                                v-if="attrsVisibility[attribute.Name] && isEditableAttribute(attribute)"
                                outlined
                                :name="attribute.Name"
                                v-model="model.attributes[attribute.Name]"
                                :items="resolveItems(attribute.Options.Option)"
                                true-value="Y"
                                :label="getAttributeLabel(attribute)"
                                false-value="N"
                                @change="computeAttributes"
                                v-validate="attributeValidationRules(attribute)"
                                :error-messages="errors.collect(attribute.Name)"
                                item-text="Title"
                                item-value="Value">
                              </component>
                            </v-col>
                          </v-row>
                        </div>
                        <div v-else class="subtitle-1">
                          <p>No additional information is needed for this Domain. Please proceed to the next step.</p>
                        </div>
                      </v-container>
                      <gg-btn secondary class="mr-1" @click="step_counter = 1">
                        Back
                      </gg-btn>
                      <gg-btn primary type="submit" @click="accountInformation">
                        Go to the payment
                      </gg-btn>
                    </v-form>
                  </v-card-text>
                </v-card>
              </div>
            </v-stepper-content>

            <v-stepper-content step="3">
              <v-card elevation="0" v-if="step_counter === 3">
                <v-card-title>
                  <p class="subtitle-1">Domain addons</p>
                </v-card-title>
                <v-card-text>
                  <div v-for="(item, key, index) in domain.options"
                       :key="index"
                  >
                    <v-checkbox
                      v-if="item.type === 'checkbox'"
                      :label="item.title"
                      :disabled="handleDisabledCheckbox(item.title)"
                      v-model="options_model[key]"
                      style="margin: 0; padding: 0"
                    />
                  </div>
                </v-card-text>
              </v-card>

              <payment-gateway
                ref="paymentGateway"
                @gateway="listenGateway"
                @card="listenCard"
                @card-switch="listenCardNew"
              >
                <template slot="header">
                  <h3>Payment method</h3>
                </template>
                <template slot="actions">
                  <gg-btn secondary @click="step_counter = 2">Back</gg-btn>
                  <gg-btn primary @click="orderDomain" :loading="buttons.purchaseDomain">Purchase domain</gg-btn>
                </template>
              </payment-gateway>
            </v-stepper-content>

            <v-stepper-content step="4">
              <div v-if="step_counter === 4">
                <v-card class="app-card card-borderless">
                  <v-card-title class="pl-0">
                    Domain Registration Order Received
                  </v-card-title>
                  <v-card-text>
                    <p>
                      Thank you! Your domain registration order has been
                      received and will be processed shortly.
                    </p>
                    <p>
                      Your order number is <strong>#{{ orderid }}</strong>
                    </p>
                  </v-card-text>
                  <v-card-actions>
                    <gg-btn color="primary" tile :to="{ name: 'domains' }">
                      Return to My Domains
                    </gg-btn>
                  </v-card-actions>
                </v-card>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import domainAttrsMixin from '@/mixins/domainAttributes'
import PaymentGateway from '@/components/Common/PaymentGateway'
import { countries } from '@/helpers/countries'
import { isProfileFilled } from 'Helpers/helpers'

export default {
  name: 'DomainRegistrationView',
  mixins: [domainAttrsMixin],
  components: { PaymentGateway },
  provide () {
    return {
      $validator: this.$validator,
    }
  },
  data: () => ({
    countries,
    step_counter: 1,
    search: '',
    domain: {},
    model: {
      firstname: '',
      lastname: '',
      email: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      postcode: '',
      phonenumber: '',
    },
    options_model: {},
    invoiceid: '',
    orderid: '',
    buttons: {
      step_one: false,
      purchaseDomain: false,
    },
    rules: {
      domain: {
        required: true,
        url: true,
      },
      required_field: {
        required: true,
      },
      credit_card: {
        card_lastfour: {
          required: true,
          credit_card: true,
        },
        card_expdate: {
          required: true,
          expiry: true,
        },
        cvc: {
          required: true,
          cvc: true,
        },
      },
    },
    isAttributes: false,
    ExtAttributes: [],
    gateway: '',
    isCardNew: false,
    card_model: {
      cardnum: '',
      cardnum_cvv: '',
      expdate: ''
    },
    empty_profile_text: 'You must complete your profile before you can register a domain.'
  }),
  computed: {
    ...mapGetters(['SessionProfile']),
    isEmpty () {
      for (let prop in this.domain) {
        if (this.domain.hasOwnProperty(prop)) {
          return false
        }
      }
      return JSON.stringify(this.domain) === JSON.stringify({})
    },
    getPrivacy () {
      return this.idProtectEligible && !this.domain.idprotect_enabled
    },
    isProfileFilled
  },
  created () {
    this.fillProfileInformation()
  },
  methods: {
    ...mapActions(['domainLookup', 'domainRegister', 'reportError', 'reportSuccess']),
    clearSearchData () {
      this.domain = {}
    },
    listenGateway (gateway) {
      this.gateway = gateway
    },
    listenCard (card) {
      this.card_model = card
    },
    listenCardNew (card) {
      this.isCardNew = card
    },
    async searchDomain (e) {
      e.preventDefault()
      const valid = await this.$validator.validateAll()
      if (valid) {
        this.buttons.step_one = true
        let response
        this.domain = {}
        try {
          response = await this.domainLookup(this.search)
        } catch (e) {
          this.reportError(e)
        }
        this.domain = response
        if (response.options) {
          for (let key in response.options) {
            if (response.options.hasOwnProperty(key)) {
              // Set options state to data model
              let defaultValue = response.options[key]?.default == '1'
              this.$set(this.options_model, key, defaultValue)
            }
          }
        }
        // attributes logic
        this.isAttributes = false
        if (typeof response.ExtAttributes === 'object' &&
            typeof response.ExtAttributes.Attribute !== 'undefined') {
          this.isAttributes = true
          this.ExtAttributes = response.ExtAttributes
          // initialise attributes
          this.model.attributes = this.ExtAttributes.Attribute.reduce(
            (carry, { Name, Options, UserDefined }) => {
              if (this.isEditableAttribute({ Name, Options, UserDefined })) {
                // TODO: use default value from response
                carry[Name] = ''
              }
              return carry
            }, {})
          this.$nextTick(() => {
            this.conditionalAttributesVisible(this.ExtAttributes.Attribute)
          })
        }

        this.buttons.step_one = false
      }
    },
    computeAttributes () {
      this.conditionalAttributesVisible(this.ExtAttributes.Attribute)
    },
    fillProfileInformation () {
      this.model.firstname = this.SessionProfile.firstname
      this.model.lastname = this.SessionProfile.lastname
      this.model.email = this.SessionProfile.email
      this.model.address1 = this.SessionProfile.address1
      this.model.address2 = this.SessionProfile.address2
      this.model.city = this.SessionProfile.city
      this.model.state = this.SessionProfile.state
      this.model.country = this.SessionProfile.country
      this.model.postcode = this.SessionProfile.postcode
      this.model.phonenumber = this.SessionProfile.phonenumber
    },
    async accountInformation (e) {
      e.preventDefault()
      try {
        const valid = await this.$validator.validateAll()
        if (valid) {
          this.step_counter = 3
        }
      } catch (e) {
        this.reportError(e)
      }
    },
    handleDisabledCheckbox (title) {
      switch (title) {
        case 'Domain Locking (Included Free)':
          return true
        case 'Total DNS Control (Included Free)':
          return true
        default:
          return false
      }
    },
    async orderDomain () {
      const valid = await this.$refs.paymentGateway.validateForm()
      if (!valid) {
        return
      }
      this.buttons.purchaseDomain = true
      try {
        let data = {}
        let whoisprivacy = false
        if (this.options_model['privacy']) {
          whoisprivacy = true
        }

        if (this.isCardNew) {
          data = {
            domain: this.domain.domain,
            options: {
              privacy: whoisprivacy
            },
            whois: this.model,
            cardnum: this.card_model.cardnum,
            expdate: this.card_model.expdate,
            cardnum_cvv: this.card_model.cardnum_cvv,
          }
        } else {
          data = {
            domain: this.domain.domain,
            options: {
              privacy: whoisprivacy
            },
            whois: this.model,
            cardnum_cvv: this.card_model.cardnum_cvv,
          }
        }

        const response = await this.domainRegister(data)
        this.invoiceid = response.invoiceid
        this.orderid = response.orderid
        this.step_counter = 4
      } catch (e) {
        this.reportError(e)
      } finally {
        this.buttons.purchaseDomain = false
      }
    },
  },
}
</script>
